import { Building2, Plus } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Combobox } from "@/components/combobox";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCanManageCompanies } from "@/features/companies/api/use-can-manage-companies";
import { useCurrentUser } from "@/hooks/use-current-user";

import {
  useBusinessTravelCompanies,
  useCreateTravelAgencyCompanyFromClaim,
  useSetTravelAgencyCompanyForClaim,
} from "../../api/use-agent-feedback";

interface SetBusinessTravelCompanyProps {
  claimId: string;
  currentTravelAgencyCompany: { id: string; displayName: string | null } | null;
}

/** Port of overview/SetBusinessTravelCompany.vue — enterprise picker with inline creation. */
export function SetBusinessTravelCompany({
  claimId,
  currentTravelAgencyCompany,
}: SetBusinessTravelCompanyProps) {
  const { can } = useCurrentUser();
  const canManageCompanies = useCanManageCompanies();
  const canEdit = can([...UPCLAIM_ROLES, "CUSTOMER_ADMIN", "CUSTOMER_ACCOUNT_MANAGER"]) || canManageCompanies;

  const [comboboxOpen, setComboboxOpen] = useState(false);
  const { data: companies = [] } = useBusinessTravelCompanies(claimId, comboboxOpen);
  const setCompany = useSetTravelAgencyCompanyForClaim(claimId);
  const createCompany = useCreateTravelAgencyCompanyFromClaim(claimId);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    displayName: "",
    domain: "",
    mainContactEmail: "",
  });

  async function createEnterprise() {
    const { success } = await createCompany.mutateAsync({
      displayName: createForm.displayName,
      domain: createForm.domain,
      mainContactEmail: createForm.mainContactEmail || null,
    });
    if (success) {
      setCreateOpen(false);
    }
  }

  return (
    <>
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>Ajouter une entreprise</DialogTitle>
          </DialogHeader>
          <DialogPanel className="space-y-2">
            <div className="flex justify-between">
              <Label htmlFor="enterprise-name" className="text-sm font-medium text-gray-900">
                Nom de l'entreprise
              </Label>
              <span className="text-sm text-gray-500">Requis</span>
            </div>
            <Input
              id="enterprise-name"
              placeholder="RadioFrance"
              value={createForm.displayName}
              onChange={(event) => setCreateForm((form) => ({ ...form, displayName: event.target.value }))}
            />
            <div className="mt-2 flex justify-between">
              <Label htmlFor="enterprise-domain" className="text-sm font-medium text-gray-900">
                Domaine
              </Label>
              <span className="text-sm text-gray-500">Requis</span>
            </div>
            <Input
              id="enterprise-domain"
              placeholder="radiofrance.fr"
              value={createForm.domain}
              onChange={(event) => setCreateForm((form) => ({ ...form, domain: event.target.value }))}
            />
            <div className="mt-2 flex justify-between">
              <Label htmlFor="enterprise-email" className="text-sm font-medium text-gray-900">
                Email du référent
              </Label>
              <span className="text-sm text-gray-500">Optionnel</span>
            </div>
            <Input
              id="enterprise-email"
              type="email"
              placeholder="francois@radiofrance.fr"
              value={createForm.mainContactEmail}
              onChange={(event) =>
                setCreateForm((form) => ({ ...form, mainContactEmail: event.target.value }))
              }
            />
          </DialogPanel>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => void createEnterprise()}
              disabled={!createForm.displayName || !createForm.domain || createCompany.isPending}
            >
              Créer
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      <Combobox
        items={companies.map((company) => ({
          id: company.id,
          displayName: company.displayName ?? company.rawGdsName ?? company.domain ?? "—",
        }))}
        value={
          currentTravelAgencyCompany
            ? {
                id: currentTravelAgencyCompany.id,
                displayName: currentTravelAgencyCompany.displayName ?? "—",
              }
            : null
        }
        onChange={(enterprise) =>
          setCompany.mutate(enterprise ? { travelAgencyCompanyId: enterprise.id } : { unset: true })
        }
        getKey={(enterprise) => enterprise.id}
        getLabel={(enterprise) => enterprise.displayName}
        renderItem={(enterprise) => (
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="truncate">{enterprise.displayName}</span>
          </span>
        )}
        placeholder="Aucune"
        searchPlaceholder="Rechercher une entreprise..."
        emptyMessage="Aucune entreprise trouvée"
        unselectLabel="Aucune"
        disabled={!canEdit}
        onOpenChange={setComboboxOpen}
        footerAction={{
          label: (
            <>
              <Plus className="size-4 shrink-0" aria-hidden="true" />
              Ajouter une entreprise
            </>
          ),
          onSelect: () => setCreateOpen(true),
        }}
      />
    </>
  );
}
